import { useState } from 'react'

import {
  reportDownloadUrl,
  useGenerateReport,
  useReportKinds,
  useReportRun,
  useReportRuns,
  useReportSchedules,
  useScheduleReport,
  type ReportDocument,
  type ReportRunRow,
} from '../api/queries'
import { useTenant } from '../api/tenant'
import type { Session } from '../api/types'
import { Failure } from '../components/Failure'

/**
 * Reports — what has been generated, and what will be.
 *
 * A run is opened in place rather than on its own route, because the thing somebody does
 * with a report is read it and then download it, and a page transition between those two
 * loses the list they were working through.
 *
 * Blind spots are shown on the list rows, not only inside the report. A reader scanning
 * six months of posture reports should be able to see which ones rest on partial data
 * without opening each one — that is the difference between a caveat and a disclaimer.
 */
export function ReportsPage({
  tenants = [],
  session,
}: {
  tenants?: string[]
  session?: Session
}) {
  const [tenant] = useTenant(tenants)
  const [openRun, setOpenRun] = useState<string | null>(null)
  const [kind, setKind] = useState('posture')
  const [cadence, setCadence] = useState('weekly')

  const kinds = useReportKinds()
  const runs = useReportRuns(tenant)
  const schedules = useReportSchedules(tenant)
  const generate = useGenerateReport(tenant)
  const schedule = useScheduleReport(tenant)

  const membership = session?.memberships.find((entry) => entry.tenant === tenant)
  const canGenerate = Boolean(membership?.can_triage)
  const canAdminister = Boolean(membership?.can_administer)

  if (runs.isError) return <Failure error={runs.error} onRetry={() => runs.refetch()} />

  return (
    <section aria-labelledby="reports-heading">
      <header
        style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h2 id="reports-heading" style={{ margin: 0 }}>
          Reports
        </h2>
        <span className="muted">
          {runs.data ? `${runs.data.results.length} generated` : 'loading'}
        </span>
      </header>

      {canGenerate && (
        <form
          className="panel"
          style={{
            marginTop: 'var(--space-4)',
            display: 'flex',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
          onSubmit={(event) => {
            event.preventDefault()
            generate.mutate({ kind })
          }}
        >
          <label htmlFor="report-kind">
            Report
            <select id="report-kind" value={kind} onChange={(event) => setKind(event.target.value)}>
              {(kinds.data?.results ?? []).map((entry) => (
                <option key={entry.kind} value={entry.kind}>
                  {entry.title}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={generate.isPending}>
            Generate now
          </button>

          {canAdminister && (
            <>
              <label htmlFor="report-cadence">
                Or every
                <select
                  id="report-cadence"
                  value={cadence}
                  onChange={(event) => setCadence(event.target.value)}
                >
                  <option value="daily">day</option>
                  <option value="weekly">week</option>
                  <option value="monthly">month</option>
                </select>
              </label>
              <button
                type="button"
                onClick={() => schedule.mutate({ kind, cadence })}
                disabled={schedule.isPending}
              >
                Schedule it
              </button>
            </>
          )}
        </form>
      )}

      {schedules.data && schedules.data.results.length > 0 && (
        <section aria-labelledby="schedules-heading" style={{ marginTop: 'var(--space-5)' }}>
          <h3 id="schedules-heading">Scheduled</h3>
          {/* Said on the screen, not only in the API docs. A schedule that looks like it
              emails a report and does not is worse than an absent feature. */}
          <p className="muted" style={{ fontSize: 'var(--text-xs)' }}>
            {schedules.data.delivery}
          </p>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {schedules.data.results.map((row) => (
              <li key={row.id} className="panel" style={{ marginBottom: 'var(--space-2)' }}>
                <span className="mono">{row.title}</span>{' '}
                <span className="muted">every {row.cadence.replace('ly', '')}</span>{' '}
                <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                  {row.last_run_at
                    ? `last ran ${new Date(row.last_run_at).toLocaleString()}`
                    : 'has not run yet'}
                </span>
                {row.last_error && (
                  // Never swallowed: a schedule that silently stops is a report somebody
                  // believes they are receiving.
                  <p style={{ margin: 0, color: 'var(--bad)', fontSize: 'var(--text-xs)' }}>
                    last run failed — {row.last_error}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-labelledby="runs-heading" style={{ marginTop: 'var(--space-5)' }}>
        <h3 id="runs-heading">Generated</h3>
        {!runs.data ? (
          <div aria-hidden="true">
            {Array.from({ length: 3 }, (_, index) => (
              <div className="skeleton" key={index} />
            ))}
          </div>
        ) : runs.data.results.length === 0 ? (
          <p className="empty">
            Nothing has been generated yet. A report is a snapshot of the numbers at the
            moment it was made, so the first one is worth making before anybody needs it.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {runs.data.results.map((run) => (
              <li key={run.id} style={{ marginBottom: 'var(--space-2)' }}>
                <Run
                  run={run}
                  tenant={tenant}
                  open={openRun === run.id}
                  onToggle={() => setOpenRun((current) => (current === run.id ? null : run.id))}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  )
}

function Run({
  run,
  tenant,
  open,
  onToggle,
}: {
  run: ReportRunRow
  tenant: string
  open: boolean
  onToggle: () => void
}) {
  return (
    <div className="panel" style={{ padding: 'var(--space-3)' }}>
      <div
        style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'baseline' }}
      >
        <button className="linklike" aria-expanded={open} onClick={onToggle}>
          {run.title}
        </button>
        <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
          {new Date(run.generated_at).toLocaleString()} ·{' '}
          {run.trigger === 'scheduled' ? 'on a schedule' : `by ${run.generated_by || 'somebody'}`}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--space-2)' }}>
          <a href={reportDownloadUrl(run.id, 'pdf', tenant)}>PDF</a>
          <a href={reportDownloadUrl(run.id, 'csv', tenant)}>CSV</a>
        </span>
      </div>

      {run.blind_spots.length > 0 && (
        // On the row, so somebody scanning six months of reports can see which ones rest
        // on partial data without opening each one.
        <p style={{ margin: 'var(--space-1) 0 0', color: 'var(--warn)', fontSize: 'var(--text-xs)' }}>
          {run.blind_spots.length} thing(s) this report does not cover
        </p>
      )}

      {open && <Document id={run.id} tenant={tenant} />}
    </div>
  )
}

function Document({ id, tenant }: { id: string; tenant: string }) {
  const query = useReportRun(id, tenant)

  if (query.isError) return <Failure error={query.error} onRetry={() => query.refetch()} />
  if (!query.data) return <div className="skeleton" aria-hidden="true" />

  const document: ReportDocument = query.data.document
  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      <p className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 0 }}>
        {document.subtitle}
      </p>

      {document.blind_spots.length > 0 && (
        <div className="error" role="note" style={{ marginBottom: 'var(--space-3)' }}>
          <strong>What this report does not cover</strong>
          <ul style={{ marginBottom: 0 }}>
            {document.blind_spots.map((spot) => (
              <li key={spot}>{spot}</li>
            ))}
          </ul>
        </div>
      )}

      {document.sections.map((section) => (
        <section key={section.heading} style={{ marginBottom: 'var(--space-4)' }}>
          <h4 style={{ marginBottom: 'var(--space-1)' }}>{section.heading}</h4>
          {section.figures.length > 0 && (
            <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
              {section.figures.map(([label, value]) => (
                <div key={label}>
                  <p style={{ margin: 0, fontSize: 'var(--text-lg)' }}>{value}</p>
                  <p className="muted" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
                    {label}
                  </p>
                </div>
              ))}
            </div>
          )}
          {section.narrative && (
            <p style={{ fontSize: 'var(--text-xs)' }}>{section.narrative}</p>
          )}
          {section.caveat && (
            <p style={{ color: 'var(--warn)', fontSize: 'var(--text-xs)', margin: 0 }}>
              {section.caveat}
            </p>
          )}
          {section.table && section.table.rows.length > 0 && (
            <div style={{ overflowX: 'auto', marginTop: 'var(--space-2)' }}>
              <table>
                <caption className="visually-hidden">{section.heading}</caption>
                <thead>
                  <tr>
                    {section.table.headers.map((header) => (
                      <th scope="col" key={header}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.table.rows.slice(0, 25).map((row, index) => (
                    <tr key={`${section.heading}-${index}`}>
                      {row.map((cell, cellIndex) => (
                        <td key={`${cellIndex}-${cell}`}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {(section.table.truncated_from > 0 || section.table.rows.length > 25) && (
                // Stated, always. A table showing the first twenty-five of two hundred
                // reads as complete.
                <p className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                  Showing {Math.min(25, section.table.rows.length)} of{' '}
                  {section.table.truncated_from || section.table.rows.length} rows — the CSV
                  export has the rest.
                </p>
              )}
            </div>
          )}
        </section>
      ))}
    </div>
  )
}
